import { Routes } from '@angular/router';
import { APP_ROUTES } from '@app/constants';
import { onboardingGuard } from './core/auth/guards/onboarding.guard';
import { learningTrackGuard, selectTrackGuard } from './core/auth/guards/learning-track.guard';
import { adminGuard } from './core/auth/guards/admin.guard';
import { authGuard } from './core/auth/guards/auth.guard';
import { superAdminGuard } from './core/auth/guards/super-admin.guard';
import { guestGuard } from './core/auth/guards/guest.guard';

/**
 * This file is EAGER — `app.config.ts` imports it at bootstrap, so anything it
 * imports statically lands in the initial bundle. That is why every branch below is
 * `loadChildren` and why the domain repository providers live in the lazily loaded
 * route files (`auth/auth.routes.ts`, `teacher/teacher.routes.ts`,
 * `student/student.routes.ts`) rather than here or in `app.config.ts`.
 *
 * Only the guards are eager, and they inject `AuthFacade` alone, which is still
 * root-provided by `provideAuth()`.
 */
export const routes: Routes = [
   {
      path: APP_ROUTES.AUTH,
      loadChildren: () => import('./auth/auth.routes').then((m) => m.authRoutes),
   },
   {
      path: APP_ROUTES.ONBOARDING,
      canActivate: [onboardingGuard],
      loadChildren: () => import('./auth/auth.routes').then((m) => m.onboardingRoutes),
   },
   {
      // The one-time learning-track chooser for students who registered before
      // the track existed. Top-level and outside `/student` for the same reason
      // `/onboarding` is: `learningTrackGuard` redirects here from that branch,
      // and nesting it inside would be a guard loop.
      path: APP_ROUTES.SELECT_TRACK,
      canActivate: [selectTrackGuard],
      loadChildren: () => import('./auth/auth.routes').then((m) => m.selectTrackRoutes),
   },
   {
      path: 'admin',
      canActivate: [superAdminGuard],
      loadChildren: () => import('./admin/admin.routes').then((m) => m.adminRoutes),
   },
   {
      path: 'teacher',
      canActivate: [adminGuard],
      loadChildren: () => import('./teacher/teacher.routes').then((m) => m.teacherRoutes),
   },
   {
      path: 'student',
      canActivate: [authGuard, learningTrackGuard],
      loadChildren: () => import('./student/student.routes').then((m) => m.studentRoutes),
   },
   {
      // Standalone info page for a teacher whose subscription lapsed. Top-level
      // (outside /teacher) and unguarded so adminGuard can redirect here without
      // a guard ping-pong.
      path: 'subscription-inactive',
      loadComponent: () =>
         import('./auth/subscription-inactive/subscription-inactive').then(
            (m) => m.SubscriptionInactive,
         ),
   },
   {
      // Public legal pages — top-level and unguarded so they open whether or
      // not someone is signed in (linked from the auth footer).
      path: APP_ROUTES.PRIVACY,
      loadComponent: () => import('./pages/legal/privacy.page').then((m) => m.PrivacyPage),
   },
   {
      path: APP_ROUTES.TERMS,
      loadComponent: () => import('./pages/legal/terms.page').then((m) => m.TermsPage),
   },
   {
      // The public, unauthenticated landing page for a teacher's own domain
      // (`teachers.domain`, resolved via `HOSTNAME` — MULTI_TENANT_PLAN.md §2).
      // `guestGuard` dispatches an already-authenticated visitor to their own
      // shell and lets a guest through, same as the auth routes. Kept as its
      // own `loadChildren` file (not a bare `loadComponent` here) so its
      // repository/facade providers stay out of this eager file — see the
      // header comment above.
      path: '',
      canActivate: [guestGuard],
      loadChildren: () => import('./pages/landing/landing.routes').then((m) => m.landingRoutes),
   },
];
